import { ContentType, Picture } from '@/@types/common'
import type { Post, PostType } from '@/@types/post'
import {
	GET_POST,
	GET_REPOST,
	LIST_COLLETIONS,
	LIST_FOLLOWING_POSTS,
	LIST_FOR_GALLERY,
	LIST_RECOMMEND_POSTS,
	LIST_USER_POSTS,
} from '@/constants/api'
import { get, getAxiosInstance, post } from '@/utils/request'
import {
	type InfiniteData,
	type UseQueryOptions,
	type UseSuspenseInfiniteQueryOptions,
} from '@tanstack/react-query'
import queryClient from '../queryClient'

export async function fetchOriginalPost(id: string) {
	return getAxiosInstance()
		.get(GET_POST, {
			params: {
				id,
			},
			suppressToast: true,
		})
		.then((res) => res.data.data as Post)
}

export async function fetchRepost(id: string) {
	return getAxiosInstance()
		.get(GET_REPOST, {
			params: {
				id,
			},
			suppressToast: true,
		})
		.then((res) => res.data.data as Post)
}

export function getUniversalPostFetcher(type: ContentType) {
	return (
		{
			ORIGINAL_POST: fetchOriginalPost,
			REPOST: fetchRepost,
		} as Record<ContentType, typeof fetchOriginalPost | typeof fetchRepost>
	)[type]
}

export async function fetchAndCachePost(id: string, type: PostType) {
	return getUniversalPostFetcher(type)(id).then((data) => {
		if (data) {
			cachePost(data)
		}
		return data
	})
}

export const CACHE_POSTS_QUERY_OPTIONS: UseQueryOptions<Record<string, Post>> = {
	queryKey: [GET_POST, GET_REPOST],
	initialData: {} as Record<string, Post>,
}

export function cachePost(data: Post) {
	return queryClient.setQueryData<Record<string, Post>>(
		CACHE_POSTS_QUERY_OPTIONS.queryKey,
		(prev) => ({
			...prev,
			[data.id]: data,
		})
	)
}
export function cachePosts(data: Post[]) {
	return queryClient.setQueryData<Record<string, Post>>(
		CACHE_POSTS_QUERY_OPTIONS.queryKey,
		(prev) => ({
			...prev,
			...Object.fromEntries(data.map((post) => [post.id, post])),
		})
	)
}
export function retrievePost(id: string): Post | undefined {
	return queryClient.getQueryData<Record<string, Post>>(CACHE_POSTS_QUERY_OPTIONS.queryKey)?.[id]
}
export function retrievePosts(ids: string[]): Post[] {
	return Object.values(
		queryClient.getQueryData<Record<string, Post>>(CACHE_POSTS_QUERY_OPTIONS.queryKey) ?? {}
	).filter((post) => ids.includes(post.id))
}

export function generatePostQueryKey(id: string | null | undefined) {
	return [GET_POST, GET_REPOST, id]
}

export interface ListPage<TData> {
	data: TData[]
	loadMoreKey?: unknown
}
export const FOLLOWING_POSTS_QUERY_OPTIONS: UseSuspenseInfiniteQueryOptions<
	ListPage<string>,
	unknown,
	InfiniteData<ListPage<string>>
> = {
	queryKey: [LIST_FOLLOWING_POSTS],
	queryFn: ({ pageParam }) =>
		post<ListPage<Post>>(LIST_FOLLOWING_POSTS, {
			limit: 20,
			loadMoreKey: pageParam,
		})
			.then((res) => res.data)
			.then((data) => {
				cachePosts(data.data)
				return {
					data: data.data.map((post) => post.id),
					loadMoreKey: data.loadMoreKey,
				}
			}),
	getNextPageParam: (lastPage: ListPage<string>) => lastPage.loadMoreKey,
	initialPageParam: undefined,
	refetchOnMount: false,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	retryOnMount: false,
}

export const EXPLORE_POSTS_QUERY_OPTIONS: UseSuspenseInfiniteQueryOptions<
	ListPage<string>,
	unknown,
	InfiniteData<ListPage<string>>
> = {
	queryKey: [LIST_RECOMMEND_POSTS],
	queryFn: ({ pageParam }) =>
		post<ListPage<Post>>(LIST_RECOMMEND_POSTS, {
			limit: 20,
			loadMoreKey: pageParam,
		})
			.then((res) => res.data)
			.then((data) => {
				cachePosts(data.data)
				return {
					data: data.data.map((post) => post.id),
					loadMoreKey: data.loadMoreKey,
				}
			}),
	getNextPageParam: (lastPage: ListPage<string>) => lastPage.loadMoreKey,
	initialPageParam: undefined,
	refetchOnMount: false,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	retryOnMount: false,
}

export const COLLETION_POSTS_QUERY_OPTIONS: UseSuspenseInfiniteQueryOptions<
	ListPage<string>,
	unknown,
	InfiniteData<ListPage<string>>
> = {
	queryKey: [LIST_COLLETIONS],
	queryFn: ({ pageParam }) =>
		post<ListPage<Post>>(LIST_COLLETIONS, {
			limit: 20,
			loadMoreKey: pageParam,
		})
			.then((res) => res.data)
			.then((data) => {
				cachePosts(data.data)
				return {
					data: data.data.map((post) => post.id),
					loadMoreKey: data.loadMoreKey,
				}
			}),
	getNextPageParam: (lastPage: ListPage<string>) => lastPage.loadMoreKey,
	initialPageParam: undefined,
	refetchOnMount: false,
	refetchOnWindowFocus: false,
	refetchOnReconnect: false,
	retryOnMount: false,
}

export function getUserPostsQueryOptions(
	username: string
): UseSuspenseInfiniteQueryOptions<ListPage<string>, unknown, InfiniteData<ListPage<string>>> {
	return {
		queryKey: [LIST_USER_POSTS, username],
		queryFn: ({ pageParam }) =>
			post<ListPage<Post>>(LIST_USER_POSTS, {
				limit: 20,
				loadMoreKey: pageParam,
				username,
			})
				.then((res) => res.data)
				.then((data) => {
					cachePosts(data.data)
					return {
						data: data.data.map((post) => post.id),
						loadMoreKey: data.loadMoreKey,
					}
				}),
		getNextPageParam: (lastPage: ListPage<string>) => lastPage.loadMoreKey,
		initialPageParam: undefined,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retryOnMount: false,
	}
}

export interface GalleryPicture {
	pictureKey: string
	pictureInfo: Picture
	postId: string
	createdAt: string
}

export interface GalleryPageData {
	pictures: GalleryPicture[]
	posts: Post[]
}

export function getListForGalleryInfiniteQueryOptions(
	username: string
): UseSuspenseInfiniteQueryOptions<
	ListPage<GalleryPageData>,
	unknown,
	InfiniteData<ListPage<GalleryPageData>>
> {
	return {
		queryKey: [LIST_FOR_GALLERY, username],
		queryFn: ({ pageParam }) =>
			post<ListPage<GalleryPageData>>(LIST_FOR_GALLERY, {
				limit: 50,
				loadMoreKey: pageParam,
				username,
			}).then((res) => res.data),
		getNextPageParam: (lastPage) => lastPage.loadMoreKey,
		initialPageParam: undefined,
	}
}
